/**
 * クラウドサイン連携で使うプロパティを HubSpot 側に一括作成する。
 *
 * 旧「プロパティ」タブから移設した。あちらはオブジェクトタイプを選んで
 * 詳細画面に入る構成だったため一括作成もその中にあったが、この作業は
 * オブジェクトタイプ横断で行うものなので、独立したセクションにしている。
 *
 * すでに存在するプロパティは HubSpot 側が 409 を返すので、
 * `already_exists` として成功扱いにする（エラーにしない）。
 */
import { useState, useEffect } from "react";
import { api } from "../api.js";
import { card, selectStyle, btnPrimary } from "./ui.js";

/** クラウドサインの結果を書き込む基本プロパティ */
const CORE_PROPERTIES = [
  {
    name: "cs_status",
    label: "クラウドサインステータス",
    type: "enumeration",
    fieldType: "select",
    options: [
      { label: "下書き", value: "draft", displayOrder: 1 },
      { label: "送信済み", value: "sent", displayOrder: 2 },
      { label: "顧客署名済", value: "signed", displayOrder: 3 },
      { label: "締結完了", value: "completed", displayOrder: 4 },
      { label: "却下", value: "declined", displayOrder: 5 },
      { label: "期限切れ", value: "expired", displayOrder: 6 },
    ],
  },
  { name: "cs_document_id", label: "クラウドサイン書類ID", type: "string", fieldType: "text" },
  { name: "cs_draft_url", label: "下書きURL", type: "string", fieldType: "text" },
  { name: "cs_completed_url", label: "締結完了URL", type: "string", fieldType: "text" },
];

/** 送信元ファイル・宛先・共有先・締結結果のファイル */
const FILE_PROPERTIES = [
  { name: "contract_file", label: "契約書", type: "string", fieldType: "file" },
  { name: "appendix_file", label: "別紙・付属書類", type: "string", fieldType: "file" },
  { name: "cs_completed_file", label: "締結完了ファイル", type: "string", fieldType: "file" },
  { name: "cs_agreement_file", label: "合意締結証明書", type: "string", fieldType: "file" },
  { name: "cs_recipient_name", label: "送付先氏名1", type: "string", fieldType: "text" },
  { name: "cs_recipient_email", label: "送付先メール1", type: "string", fieldType: "text" },
  { name: "cs_recipient_name_2", label: "送付先氏名2", type: "string", fieldType: "text" },
  { name: "cs_recipient_email_2", label: "送付先メール2", type: "string", fieldType: "text" },
  { name: "cs_share_recipient_name", label: "共有先氏名1", type: "string", fieldType: "text" },
  { name: "cs_share_recipient_email", label: "共有先メール1", type: "string", fieldType: "text" },
  { name: "cs_share_recipient_name_2", label: "共有先氏名2", type: "string", fieldType: "text" },
  { name: "cs_share_recipient_email_2", label: "共有先メール2", type: "string", fieldType: "text" },
];

const ALL_PROPERTIES = [...CORE_PROPERTIES, ...FILE_PROPERTIES];

interface ObjectOption {
  key: string;
  label: string;
}

export default function BulkPropertyCreate() {
  const [objects, setObjects] = useState<ObjectOption[]>([]);
  const [objectKey, setObjectKey] = useState("");
  const [creating, setCreating] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getObjectSchemas()
      .then((schemas) => {
        const list = [
          ...schemas.standard.map((s: any) => ({ key: s.key, label: s.label })),
          ...schemas.custom.map((s: any) => ({ key: s.key, label: `${s.label}（カスタム）` })),
        ];
        setObjects(list);
        // 取引が最も使われるので既定にする
        setObjectKey(list.find((o) => o.key === "deals")?.key ?? list[0]?.key ?? "");
      })
      .catch((e) => setError(e.message));
  }, []);

  const objectLabel = objects.find((o) => o.key === objectKey)?.label ?? objectKey;

  const run = async () => {
    setError(null);
    setResult(null);
    let created = 0;
    let skipped = 0;

    for (const prop of ALL_PROPERTIES) {
      try {
        setCreating(prop.name);
        const body: any = {
          name: prop.name,
          label: prop.label,
          type: prop.type,
          fieldType: prop.fieldType,
        };
        if ((prop as any).options) body.options = (prop as any).options;
        const res = await api.createProperty(objectKey, body);
        // すでに存在する場合も成功扱い（HubSpot は 409 を返す）
        if (res.status === "created") created++;
        else skipped++;
      } catch {
        skipped++;
      }
    }

    setCreating(null);
    setResult(`${objectLabel} に ${created} 件作成しました（${skipped} 件は既存またはスキップ）`);
  };

  return (
    <div style={card}>
      <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>プロパティの一括作成</div>
      <div style={{ fontSize: 13, color: "#6b6b68", marginBottom: 16, lineHeight: 1.7 }}>
        クラウドサイン連携で使うプロパティ（{ALL_PROPERTIES.length} 件）を HubSpot に作成します。
        すでに存在するプロパティはスキップされるため、何度実行しても問題ありません。
        <br />
        作成後、上の「クライアントID一覧」にある「デフォルト設定」の「編集」から、各プロパティの用途（格納先・宛先など）を割り当ててください。
      </div>

      {error && (
        <div style={{ padding: "10px 14px", borderRadius: 8, background: "#FCEBEB", color: "#A32D2D", fontSize: 13, marginBottom: 14 }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <select
          style={{ ...selectStyle, width: 260 }}
          value={objectKey}
          disabled={!!creating}
          onChange={(e) => { setObjectKey(e.target.value); setResult(null); }}
        >
          {objects.map((o) => (
            <option key={o.key} value={o.key}>{o.label}</option>
          ))}
        </select>
        <button
          style={{ ...btnPrimary, opacity: creating || !objectKey ? 0.6 : 1 }}
          disabled={!!creating || !objectKey}
          onClick={run}
        >
          {creating ? `作成中（${creating}）...` : "このオブジェクトに一括作成"}
        </button>
        {result && <span style={{ fontSize: 14, color: "#0F6E56", fontWeight: 600 }}>{result}</span>}
      </div>

      <div style={{ marginTop: 14, padding: "10px 14px", background: "#faf9f7", borderRadius: 8, fontSize: 12, color: "#6b6b68", lineHeight: 1.8 }}>
        HubSpot の無料プランではカスタムプロパティが 10 件までのため、
        途中で作成が止まることがあります。その場合は不要なプロパティを個別に作成してください。
      </div>
    </div>
  );
}
